import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { ApiResponse } from '../../shared/models/common.model';
import { AuthService } from '../services/auth/auth.service';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import { ENVIRONMENT } from '../tokens/environment.token';

/** The HTTP header key used for authorization. */
export const AUTH_TOKEN_HEADER_KEY = 'Authorization';

/**
 * Sentinel emitted on the refresh subject when refresh fails so queued
 * waiters do not hang forever on `filter(token !== null)`.
 */
const REFRESH_FAILED = '';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Functional AuthInterceptor
 *
 * Appends Authorization headers and handles JWT token refreshes automatically.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const localStorageService = inject(LocalStorageService);
  const router = inject(Router);
  const env = inject(ENVIRONMENT);
  const authService = inject(AuthService);

  const token = localStorageService.getToken();
  const authReq = addHeaders(req, token, localStorageService, env);

  return next(authReq).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        !authReq.url.includes('auth/login') &&
        !authReq.url.includes('auth/refresh') &&
        !authReq.url.includes('auth/forgot-password') &&
        !authReq.url.includes('auth/register') &&
        !authReq.url.includes('auth/verify-otp') &&
        !authReq.url.includes('auth/reset-password') &&
        error.status === 401
      ) {
        return handle401Error(authReq, next, localStorageService, router, authService, env);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Handles a 401 Unauthorized error by attempting to refresh the user session.
 * Queues concurrent requests while the refresh call is active.
 *
 * @param request - Original HTTP request that failed
 * @param next - Next HTTP handler function
 * @param localStorageService - Local storage service to retrieve refresh tokens
 * @param router - Angular Router for redirection on failure
 * @param authService - Auth service to invoke the refresh endpoint
 * @param env - Application environment configuration
 * @returns Observable of the retried HTTP event
 */
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  localStorageService: LocalStorageService,
  router: Router,
  authService: AuthService,
  env: { platform: string }
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = localStorageService.getRefreshToken();

    if (!refreshToken) {
      return failRefreshAndLogout(localStorageService, router);
    }

    return authService.refreshToken(refreshToken).pipe(
      switchMap((res: ApiResponse<{ accessToken?: string; authToken?: string; refreshToken?: string }>): Observable<HttpEvent<unknown>> => {
        const newToken = res.data?.accessToken || res.data?.authToken;

        if (res.success && newToken) {
          isRefreshing = false;
          localStorageService.saveAuthToken(newToken);

          if (res.data?.refreshToken) {
            localStorageService.saveRefreshToken(res.data.refreshToken);
          }

          refreshTokenSubject.next(newToken);
          return next(addHeaders(request, newToken, localStorageService, env));
        }

        return failRefreshAndLogout(localStorageService, router);
      }),
      catchError((): Observable<HttpEvent<unknown>> => {
        return failRefreshAndLogout(localStorageService, router);
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token): Observable<HttpEvent<unknown>> => {
      if (!token) {
        return logoutAndRedirect(localStorageService, router);
      }
      return next(addHeaders(request, token, localStorageService, env));
    })
  );
}

/**
 * Resets the refresh lock, notifies queued waiters of failure, and signs out.
 *
 * @param localStorageService - Local storage service to sign out
 * @param router - Angular Router to navigate to the login page
 * @returns Empty observable after redirecting to login (no error toast)
 */
function failRefreshAndLogout(
  localStorageService: LocalStorageService,
  router: Router
): Observable<never> {
  isRefreshing = false;
  refreshTokenSubject.next(REFRESH_FAILED);
  return logoutAndRedirect(localStorageService, router);
}

/**
 * Clears the local session and redirects to login without throwing a synthetic
 * HttpErrorResponse (which surfaces as "Http failure response for (unknown url)").
 *
 * @param localStorageService - Local storage service to sign out
 * @param router - Angular Router to navigate to the login page
 * @returns Empty observable so subscribers complete quietly
 */
function logoutAndRedirect(localStorageService: LocalStorageService, router: Router): Observable<never> {
  localStorageService.signOut();
  router.navigate(['/auth/login'], {
    queryParams: { r: router.url },
    replaceUrl: true,
  });
  return EMPTY as Observable<never>;
}

/**
 * Clones the request and appends headers like Authorization, Accept-Language, timezone, and platform.
 *
 * @param request - Original HTTP request
 * @param token - Access token to attach
 * @param localStorageService - Local storage service to retrieve language preferences
 * @param env - Application environment configuration
 * @returns Cloned request with updated headers
 */
function addHeaders(
  request: HttpRequest<unknown>,
  token: string,
  localStorageService: LocalStorageService,
  env: { platform: string }
): HttpRequest<unknown> {
  const lang = localStorageService.getActiveLanguage();

  let headers = request.headers
    .set('Accept-Language', lang || 'en')
    .set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)
    .set('platform', env.platform);

  // Auth bootstrap endpoints: keep any caller-supplied Bearer (refresh / OTP / reset)
  // and do not overwrite with the access token.
  if (
    request.url.includes('auth/refresh') ||
    request.url.includes('auth/login') ||
    request.url.includes('auth/forgot-password') ||
    request.url.includes('auth/verify-otp') ||
    request.url.includes('auth/reset-password') ||
    request.url.includes('auth/resend-otp')
  ) {
    return request.clone({ headers });
  }

  // Always apply the access token when present so 401 retries after refresh
  // replace the expired Bearer instead of preserving it on the cloned request.
  if (token) {
    headers = headers.set(AUTH_TOKEN_HEADER_KEY, 'Bearer ' + token);
  } else if (request.headers.has(AUTH_TOKEN_HEADER_KEY)) {
    return request.clone({ headers });
  } else {
    headers = headers.set(AUTH_TOKEN_HEADER_KEY, 'no');
  }

  return request.clone({ headers });
}
